import { hasRole } from '../../guards/auth/roles';
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, Subscription, zip } from 'rxjs';
import { MainService } from '../main.service';
import {
  CreateSendout,
  Sendout,
  SendoutStatus,
  SendoutStatusEnum,
  SendoutType,
  UpdateSendout,
} from '../../types';
import {
  GlobalDataService,
  SessionData,
} from '../global-data/global-data.service';
import { StoreService } from '../store/store.service';
import { addDays, formatISO, parseISO, startOfDay } from 'date-fns';

type RawSendout = {
  id: number;
  sendoutType: SendoutType;
  sendoutTemplateId: number;
  storeIds: number[];
  createdAt: string;
  createdBy: number;
  updatedByName: string;
  createdByName: string | null;
  updatedAt: string;
  updatedBy: number;
  reviewedAt: string | null;
  reviewedBy: number | null;
  reviewedByName: string | null;
  sendoutName: string;
  status: SendoutStatusEnum;
  eventId: string | null;
  contentSubjectLine: string;
  contentPreheader: string;
  contentHeadline: string;
  contentBodyCopy: string;
  contentCtaText: string;
  contentCtaUrl: string;
  isDeleted: boolean;
  contentImageUrl: string;
  contentDisclaimer: string;
  contactPerson: string | null;
  sendoutDate: string;
};

type SendoutFilter = (s: RawSendout) => boolean;

export enum SendoutDateLockReason {
  Past,
  Sunday,
  OtherSendoutOnSameDay,
  TooCloseToOtherSendout,
  TooCloseToToday,
}

export type SendoutDateLock = {
  date: Date;
  softLock: boolean;
  reason: SendoutDateLockReason;
};

@Injectable({
  providedIn: 'root',
})
export class SendoutService implements OnDestroy {
  private sessionDataSubscription: Subscription;
  private sessionData?: SessionData;

  private cachedSendouts: Sendout[] = [];
  private cachedSendoutsRefreshed: Date = new Date(0);
  private cachedSendoutsTTLMinutes = 5;

  constructor(
    private http: HttpClient,
    private mainService: MainService,
    private globalData: GlobalDataService,
    private storeService: StoreService
  ) {
    this.sessionDataSubscription = this.globalData
      .getSessionData()
      .subscribe((sessionData) => {
        if (this.sessionData?.store?.id !== sessionData?.store?.id) {
          this.cachedSendouts = [];
          this.cachedSendoutsRefreshed = new Date(0);
        }
        this.sessionData = sessionData;
      });
  }

  ngOnDestroy(): void {
    this.sessionDataSubscription?.unsubscribe();
  }

  fetchAllSendouts(
    fromDate: Date,
    toDate: Date | null,
    storeId?: number
  ): Observable<Sendout[]> {
    // Set up filter - default to all sendouts
    let filter: SendoutFilter = (s) => true;

    // Filter sendouts by storeId
    if (
      this.sessionData && hasRole(this.sessionData, 'SendoutManager') && !storeId
    ) {
      // Disregard drafts for HQ user
      filter = (s: RawSendout) => {
        if (s.sendoutType === SendoutType.SingleStore) {
          return s.status !== SendoutStatus.Draft;
        }
        return true;
      };
    }
    let url = `${this.mainService.getApiURL()}Sendout/Sendouts?from-date=${formatISO(
      fromDate,
      { representation: 'date' }
    )}`;
    if (toDate) {
      url = `${url}&to-date=${formatISO(toDate, { representation: 'date' })}`;
    }
    if (storeId) {
      url = `${url}&store-id=${storeId}`;
    }
    return this.http
      .get<RawSendout[]>(url, {
        headers: this.mainService.getHeaders(),
      })
      .pipe(
        map((sendouts) =>
          sendouts.filter(filter).map((sendout) => {
            const normalizedSendout: Sendout = {
              id: sendout.id,
              sendoutType: sendout.sendoutType,
              sendoutTemplateId: sendout.sendoutTemplateId,
              storeIds: sendout.storeIds,
              createdAt: parseISO(sendout.createdAt),
              createdBy: sendout.createdBy,
              updatedByName: sendout.updatedByName,
              createdByName: sendout.createdByName,
              updatedAt: parseISO(sendout.updatedAt),
              updatedBy: sendout.updatedBy,
              reviewedAt: sendout.reviewedAt
                ? parseISO(sendout.reviewedAt)
                : null,
              reviewedBy: sendout.reviewedBy,
              reviewedByName: sendout.reviewedByName || '',
              sendoutName: sendout.sendoutName,
              status: sendout.status,
              eventId: sendout.eventId,
              contentSubjectLine: sendout.contentSubjectLine,
              contentPreheader: sendout.contentPreheader,
              contentHeadline: sendout.contentHeadline,
              contentBodyCopy: sendout.contentBodyCopy,
              contentCtaText: sendout.contentCtaText,
              contentCtaUrl: sendout.contentCtaUrl,
              contentImageUrl: sendout.contentImageUrl,
              contentDisclaimer: sendout.contentDisclaimer,
              contactPerson: sendout.contactPerson || '',
              sendoutDate: parseISO(sendout.sendoutDate),
              isDeleted: sendout.isDeleted,
            };

            return normalizedSendout;
          })
        )
      );
  }

  getSendouts(
    fromDate: Date,
    toDate: Date | null,
    storeId?: number
  ): Observable<Sendout[]> {
    return this.fetchAllSendouts(fromDate, toDate, storeId).pipe(
      map((sendouts) => {
        const status = new Set<string>();
        this.cachedSendouts = sendouts;
        this.cachedSendoutsRefreshed = new Date();
        sendouts.forEach((sendout) => status.add(sendout.status));
        return sendouts;
      })
    );
  }

  getSendoutById(id: number): Observable<Sendout> {
    return this.http
      .get<RawSendout>(this.mainService.getApiURL() + `Sendout/${id}`, {
        headers: this.mainService.getHeaders(),
      })
      .pipe(
        map((sendout: RawSendout) => {
          const normalizedSendout: Sendout = {
            id: sendout.id,
            sendoutType: sendout.sendoutType,
            sendoutTemplateId: sendout.sendoutTemplateId,
            storeIds: sendout.storeIds,
            createdAt: parseISO(sendout.createdAt),
            createdBy: sendout.createdBy,
            updatedByName: sendout.updatedByName,
            createdByName: sendout.createdByName,
            updatedAt: parseISO(sendout.updatedAt),
            updatedBy: sendout.updatedBy,
            reviewedAt: sendout.reviewedAt
              ? parseISO(sendout.reviewedAt)
              : null,
            reviewedBy: sendout.reviewedBy,
            reviewedByName: sendout.reviewedByName || '',
            sendoutName: sendout.sendoutName,
            status: sendout.status,
            eventId: sendout.eventId,
            contentSubjectLine: sendout.contentSubjectLine,
            contentPreheader: sendout.contentPreheader,
            contentHeadline: sendout.contentHeadline,
            contentBodyCopy: sendout.contentBodyCopy,
            contentCtaText: sendout.contentCtaText,
            contentCtaUrl: sendout.contentCtaUrl,
            contentImageUrl: sendout.contentImageUrl,
            contentDisclaimer: sendout.contentDisclaimer,
            contactPerson: sendout.contactPerson || '',
            sendoutDate: parseISO(sendout.sendoutDate),
            isDeleted: sendout.isDeleted,
          };

          return normalizedSendout;
        })
      );
  }

  deleteSendoutById(id: number): Observable<any> {
    this.cachedSendoutsRefreshed = new Date(0);
    return this.http.delete<any>(
      this.mainService.getApiURL() + `Sendout/${id}`,
      {
        headers: this.mainService.getHeaders(),
      }
    );
  }

  createSendout(body: CreateSendout): Observable<Sendout> {
    this.cachedSendoutsRefreshed = new Date(0);
    return this.http.post<Sendout>(
      this.mainService.getApiURL() + 'Sendout',
      body,
      {
        headers: this.mainService.getHeaders(),
      }
    );
  }

  updateSendout(body: UpdateSendout): Observable<Sendout> {
    this.cachedSendoutsRefreshed = new Date(0);
    return this.http.put<Sendout>(
      this.mainService.getApiURL() + 'Sendout',
      body,
      {
        headers: this.mainService.getHeaders(),
      }
    );
  }

  getLockPeriods(sendoutDate: Date): Date[] {
    const startLock = addDays(sendoutDate, -7);
    const endLock = addDays(sendoutDate, 7);

    const lockPeriods: Date[] = [];
    for (let date = startLock; date <= endLock; date = addDays(date, 1)) {
      lockPeriods.push(startOfDay(date));
    }
    return lockPeriods;
  }
}
